/**
 * Curated unicode emoji catalog for the Slack reply composer palette.
 * Kept in-repo (no emoji-mart) to stay light; searchable by English keywords.
 */

export interface ComposerEmojiCategory {
  id: string;
  label: string;
  /** Category tab glyph */
  icon: string;
  emojis: Array<{ char: string; keywords: string }>;
}

export const COMPOSER_EMOJI_CATEGORIES: ComposerEmojiCategory[] = [
  {
    id: 'smileys',
    label: 'Caras',
    icon: '😀',
    emojis: [
      { char: '😀', keywords: 'grin happy smile' },
      { char: '😃', keywords: 'smile happy' },
      { char: '😄', keywords: 'smile laugh' },
      { char: '😁', keywords: 'grin' },
      { char: '😆', keywords: 'laugh' },
      { char: '😅', keywords: 'sweat nervous' },
      { char: '😂', keywords: 'joy tears laugh' },
      { char: '🤣', keywords: 'rofl laugh' },
      { char: '😊', keywords: 'blush smile' },
      { char: '😇', keywords: 'angel innocent' },
      { char: '🙂', keywords: 'smile' },
      { char: '😉', keywords: 'wink' },
      { char: '😍', keywords: 'heart eyes love' },
      { char: '🥰', keywords: 'love hearts' },
      { char: '😘', keywords: 'kiss' },
      { char: '😗', keywords: 'kiss' },
      { char: '😋', keywords: 'yum tasty' },
      { char: '😜', keywords: 'wink tongue' },
      { char: '🤪', keywords: 'zany crazy' },
      { char: '😝', keywords: 'tongue' },
      { char: '🤑', keywords: 'money' },
      { char: '🤗', keywords: 'hug' },
      { char: '🤭', keywords: 'oops giggle' },
      { char: '🤔', keywords: 'thinking hmm' },
      { char: '🤨', keywords: 'raised eyebrow' },
      { char: '😐', keywords: 'neutral' },
      { char: '😑', keywords: 'expressionless' },
      { char: '😶', keywords: 'silent' },
      { char: '🙄', keywords: 'eye roll' },
      { char: '😏', keywords: 'smirk' },
      { char: '😣', keywords: 'persevere' },
      { char: '😥', keywords: 'sad sweat' },
      { char: '😮', keywords: 'surprised wow' },
      { char: '😯', keywords: 'hushed' },
      { char: '😪', keywords: 'sleepy' },
      { char: '😫', keywords: 'tired' },
      { char: '🥱', keywords: 'yawn' },
      { char: '😴', keywords: 'sleep' },
      { char: '😌', keywords: 'relieved' },
      { char: '😛', keywords: 'tongue' },
      { char: '😓', keywords: 'downcast' },
      { char: '😕', keywords: 'confused' },
      { char: '🙃', keywords: 'upside down' },
      { char: '🫠', keywords: 'melting' },
      { char: '😲', keywords: 'astonished' },
      { char: '😢', keywords: 'cry sad' },
      { char: '😭', keywords: 'sob cry' },
      { char: '😤', keywords: 'triumph steam' },
      { char: '😠', keywords: 'angry' },
      { char: '😡', keywords: 'rage mad' },
      { char: '🤬', keywords: 'cursing' },
      { char: '😈', keywords: 'devil smile' },
      { char: '👿', keywords: 'angry devil' },
      { char: '💀', keywords: 'skull dead' },
      { char: '☠️', keywords: 'skull crossbones' },
      { char: '💩', keywords: 'poop' },
      { char: '🤡', keywords: 'clown' },
      { char: '👹', keywords: 'ogre' },
      { char: '👻', keywords: 'ghost' },
      { char: '👽', keywords: 'alien' },
      { char: '🤖', keywords: 'robot' },
      { char: '👀', keywords: 'eyes look' },
      { char: '🙈', keywords: 'see no evil monkey' },
      { char: '🙉', keywords: 'hear no evil' },
      { char: '🙊', keywords: 'speak no evil' },
    ],
  },
  {
    id: 'gestures',
    label: 'Gestos',
    icon: '👍',
    emojis: [
      { char: '👍', keywords: 'thumbs up yes ok' },
      { char: '👎', keywords: 'thumbs down no' },
      { char: '👏', keywords: 'clap applause' },
      { char: '🙌', keywords: 'raised hands celebrate' },
      { char: '🤝', keywords: 'handshake deal' },
      { char: '🙏', keywords: 'pray thanks please' },
      { char: '💪', keywords: 'muscle strong' },
      { char: '✌️', keywords: 'peace victory' },
      { char: '🤞', keywords: 'fingers crossed luck' },
      { char: '🤟', keywords: 'love you gesture' },
      { char: '🤘', keywords: 'rock on' },
      { char: '👌', keywords: 'ok perfect' },
      { char: '🤌', keywords: 'pinched fingers' },
      { char: '👆', keywords: 'point up' },
      { char: '👇', keywords: 'point down' },
      { char: '👈', keywords: 'point left' },
      { char: '👉', keywords: 'point right' },
      { char: '👋', keywords: 'wave hello bye' },
      { char: '🤚', keywords: 'raised hand' },
      { char: '🖐️', keywords: 'hand' },
      { char: '✋', keywords: 'stop hand' },
      { char: '🖖', keywords: 'vulcan' },
      { char: '🤙', keywords: 'call me' },
      { char: '👊', keywords: 'fist bump' },
      { char: '🤛', keywords: 'left fist' },
      { char: '🤜', keywords: 'right fist' },
      { char: '🫶', keywords: 'heart hands' },
      { char: '✍️', keywords: 'writing' },
      { char: '💅', keywords: 'nails' },
      { char: '🤳', keywords: 'selfie' },
    ],
  },
  {
    id: 'hearts',
    label: 'Corazones',
    icon: '❤️',
    emojis: [
      { char: '❤️', keywords: 'heart red love' },
      { char: '🧡', keywords: 'orange heart' },
      { char: '💛', keywords: 'yellow heart' },
      { char: '💚', keywords: 'green heart' },
      { char: '💙', keywords: 'blue heart' },
      { char: '💜', keywords: 'purple heart' },
      { char: '🖤', keywords: 'black heart' },
      { char: '🤍', keywords: 'white heart' },
      { char: '🤎', keywords: 'brown heart' },
      { char: '💔', keywords: 'broken heart' },
      { char: '❣️', keywords: 'heart exclamation' },
      { char: '💕', keywords: 'two hearts' },
      { char: '💞', keywords: 'revolving hearts' },
      { char: '💓', keywords: 'beating heart' },
      { char: '💗', keywords: 'growing heart' },
      { char: '💖', keywords: 'sparkling heart' },
      { char: '💘', keywords: 'cupid' },
      { char: '💝', keywords: 'gift heart' },
      { char: '💟', keywords: 'heart decoration' },
      { char: '✨', keywords: 'sparkles' },
      { char: '⭐', keywords: 'star' },
      { char: '🌟', keywords: 'glowing star' },
      { char: '💫', keywords: 'dizzy' },
      { char: '🔥', keywords: 'fire lit hot' },
      { char: '💯', keywords: 'hundred score' },
      { char: '💥', keywords: 'boom collision' },
      { char: '💢', keywords: 'anger' },
      { char: '💤', keywords: 'zzz sleep' },
    ],
  },
  {
    id: 'objects',
    label: 'Objetos',
    icon: '🎉',
    emojis: [
      { char: '🎉', keywords: 'party tada celebrate' },
      { char: '🎊', keywords: 'confetti' },
      { char: '🎈', keywords: 'balloon' },
      { char: '🎁', keywords: 'gift present' },
      { char: '🏆', keywords: 'trophy win' },
      { char: '🥇', keywords: 'gold medal' },
      { char: '🎯', keywords: 'target bullseye' },
      { char: '✅', keywords: 'check done yes' },
      { char: '❌', keywords: 'x cross no' },
      { char: '⚠️', keywords: 'warning' },
      { char: '🚫', keywords: 'prohibited' },
      { char: '💡', keywords: 'idea bulb' },
      { char: '📌', keywords: 'pin' },
      { char: '📍', keywords: 'location pin' },
      { char: '📎', keywords: 'paperclip' },
      { char: '🔗', keywords: 'link' },
      { char: '📝', keywords: 'memo note' },
      { char: '📋', keywords: 'clipboard' },
      { char: '📁', keywords: 'folder' },
      { char: '📂', keywords: 'open folder' },
      { char: '🗂️', keywords: 'dividers' },
      { char: '📊', keywords: 'chart bar' },
      { char: '📈', keywords: 'chart up' },
      { char: '📉', keywords: 'chart down' },
      { char: '⏰', keywords: 'alarm clock' },
      { char: '⏳', keywords: 'hourglass' },
      { char: '🚀', keywords: 'rocket launch ship' },
      { char: '💻', keywords: 'laptop computer' },
      { char: '🖥️', keywords: 'desktop' },
      { char: '📱', keywords: 'phone mobile' },
      { char: '⌨️', keywords: 'keyboard' },
      { char: '🖱️', keywords: 'mouse' },
      { char: '🔧', keywords: 'wrench fix' },
      { char: '🛠️', keywords: 'tools' },
      { char: '⚙️', keywords: 'gear settings' },
      { char: '🔒', keywords: 'lock' },
      { char: '🔓', keywords: 'unlock' },
      { char: '🔑', keywords: 'key' },
      { char: '🐛', keywords: 'bug' },
      { char: '🧪', keywords: 'test tube' },
      { char: '📦', keywords: 'package box' },
      { char: '☕', keywords: 'coffee' },
      { char: '🍺', keywords: 'beer' },
      { char: '🍕', keywords: 'pizza' },
    ],
  },
  {
    id: 'symbols',
    label: 'Símbolos',
    icon: '💬',
    emojis: [
      { char: '💬', keywords: 'speech bubble chat' },
      { char: '💭', keywords: 'thought' },
      { char: '🗯️', keywords: 'anger bubble' },
      { char: '🔈', keywords: 'speaker' },
      { char: '🔇', keywords: 'mute' },
      { char: '🔔', keywords: 'bell' },
      { char: '🔕', keywords: 'no bell' },
      { char: '📣', keywords: 'megaphone' },
      { char: '📢', keywords: 'loudspeaker' },
      { char: '🔴', keywords: 'red circle' },
      { char: '🟠', keywords: 'orange circle' },
      { char: '🟡', keywords: 'yellow circle' },
      { char: '🟢', keywords: 'green circle' },
      { char: '🔵', keywords: 'blue circle' },
      { char: '🟣', keywords: 'purple circle' },
      { char: '⚫', keywords: 'black circle' },
      { char: '⚪', keywords: 'white circle' },
      { char: '🟥', keywords: 'red square' },
      { char: '🟩', keywords: 'green square' },
      { char: '🟦', keywords: 'blue square' },
      { char: '⬆️', keywords: 'up arrow' },
      { char: '⬇️', keywords: 'down arrow' },
      { char: '⬅️', keywords: 'left arrow' },
      { char: '➡️', keywords: 'right arrow' },
      { char: '↩️', keywords: 'return' },
      { char: '↪️', keywords: 'left hook' },
      { char: '➕', keywords: 'plus' },
      { char: '➖', keywords: 'minus' },
      { char: '✖️', keywords: 'multiply' },
      { char: '➗', keywords: 'divide' },
      { char: '❓', keywords: 'question' },
      { char: '❗', keywords: 'exclamation' },
      { char: '‼️', keywords: 'double exclamation' },
      { char: '⁉️', keywords: 'interrobang' },
      { char: '™️', keywords: 'tm' },
      { char: '©️', keywords: 'copyright' },
      { char: '®️', keywords: 'registered' },
    ],
  },
];

export function filterComposerEmojis(
  query: string,
  categories: ComposerEmojiCategory[] = COMPOSER_EMOJI_CATEGORIES,
): Array<{ char: string; keywords: string; categoryId: string }> {
  const q = query.trim().toLowerCase();
  const out: Array<{ char: string; keywords: string; categoryId: string }> = [];
  for (const cat of categories) {
    for (const emoji of cat.emojis) {
      if (!q || emoji.keywords.includes(q) || emoji.char.includes(q)) {
        out.push({ ...emoji, categoryId: cat.id });
      }
    }
  }
  return out;
}
